import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Wine, Building2, MapPin, ArrowRight, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SetupPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [orgName, setOrgName] = useState("");
  const [venueName, setVenueName] = useState("");
  const [loading, setLoading] = useState(false);
  const [joinCode, setJoinCode] = useState("");

  const handleSetup = async () => {
    if (!orgName.trim() || !venueName.trim()) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/org/setup`, {
        org_name: orgName.trim(),
        venue_name: venueName.trim(),
      });
      setJoinCode(res.data.venue?.join_code || "");
      setStep(3);
      toast.success("Organisation created!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Setup failed");
    } finally {
      setLoading(false);
    }
  };

  const copyJoinLink = () => {
    const link = `${window.location.origin}/join/${joinCode}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Join link copied!"));
  };

  return (
    <div className="min-h-screen bg-[hsl(var(--background))] flex flex-col">
      <header className="p-6">
        <div className="flex items-center gap-2">
          <Wine className="text-[hsl(var(--primary))]" size={32} />
          <span className="text-2xl font-bold tracking-tight text-[hsl(var(--primary))]">FlashSpecs</span>
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          {/* Progress */}
          <div className="flex items-center justify-center gap-2 mb-8">
            {[1, 2, 3].map(s => (
              <div key={s} className={`flex items-center ${s < 3 ? 'gap-2' : ''}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                  step >= s ? 'bg-[hsl(var(--primary))] text-black' : 'bg-[hsl(var(--secondary))] text-[hsl(var(--muted-foreground))]'
                }`}>
                  {step > s ? <Check size={16} /> : s}
                </div>
                {s < 3 && <div className={`w-12 h-0.5 ${step > s ? 'bg-[hsl(var(--primary))]' : 'bg-[hsl(var(--secondary))]'}`} />}
              </div>
            ))}
          </div>

          <Card className="bg-[hsl(var(--card))] border-[hsl(var(--border))]">
            <CardContent className="p-8">
              {step === 1 && (
                <div className="space-y-6">
                  <div className="text-center">
                    <Building2 size={40} className="mx-auto text-[hsl(var(--primary))] mb-3" />
                    <h2 className="text-xl font-bold">Your Organisation</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">What's your company or group called?</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Organisation Name</Label>
                    <Input value={orgName} onChange={e => setOrgName(e.target.value)} placeholder="e.g. The Alchemist Group" className="bg-[hsl(var(--secondary))]" data-testid="setup-org-name" />
                  </div>
                  <Button onClick={() => setStep(2)} disabled={!orgName.trim()} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="setup-next-1">
                    Next <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-6">
                  <div className="text-center">
                    <MapPin size={40} className="mx-auto text-[hsl(var(--primary))] mb-3" />
                    <h2 className="text-xl font-bold">Your First Venue</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Add your first location. You can add more later.</p>
                  </div>
                  <div className="space-y-2">
                    <Label>Venue Name</Label>
                    <Input value={venueName} onChange={e => setVenueName(e.target.value)} placeholder="e.g. The Alchemist Cardiff" className="bg-[hsl(var(--secondary))]" data-testid="setup-venue-name" />
                  </div>
                  <Button onClick={handleSetup} disabled={!venueName.trim() || loading} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="setup-create">
                    {loading ? "Setting up..." : "Create Organisation"} <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-6">
                  <div className="text-center">
                    <Check size={40} className="mx-auto text-green-400 mb-3" />
                    <h2 className="text-xl font-bold">You're All Set!</h2>
                    <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">Share this link with your staff to join {venueName}:</p>
                  </div>
                  <div className="p-3 rounded-lg bg-[hsl(var(--secondary))] text-center">
                    <p className="text-sm font-mono break-all text-[hsl(var(--primary))]" data-testid="setup-join-link">
                      {window.location.origin}/join/{joinCode}
                    </p>
                  </div>
                  <Button onClick={copyJoinLink} variant="outline" className="w-full" data-testid="setup-copy-link">
                    Copy Join Link
                  </Button>
                  <Button onClick={() => navigate("/dashboard")} className="w-full bg-[hsl(var(--primary))] text-black" data-testid="setup-go-dashboard">
                    Go to Dashboard <ArrowRight size={16} className="ml-2" />
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
}
